import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Stack,
    Typography,
    Divider,
    Chip,
} from "@mui/material";
import { Localized } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ComputerizedSystem, loadComputerizedSystemById } from "./computerizedSystemsApi";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { ObjectValidationStatus } from "../project/validationProcess/validationProcessApi";
import { ObjectValidationStatusChip } from "./components/ObjectValidationStatusChip";
import { GampCategoryChip } from "./components/GampCategoryChip";
import DnsIcon from "@mui/icons-material/Dns";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { StructureName } from "../device/Structure";
import { selectSettings } from "../settings/settingsSlice";
import { format } from "date-fns";
import { GampCategory } from "./gampDictionary";
import { ComputerizedSystemOverviewPanel } from "./panels/ComputerizedSystemOverviewPanel";
import { SystemComponentsPanel } from "./panels/SystemComponentsPanel";
import { ValidationProcessesPanel } from "./panels/ValidationProcessesPanel";
import produce from "immer";
import CategoryIcon from '@mui/icons-material/Category';
import FactCheckIcon from '@mui/icons-material/FactCheck';

export const ComputerizedSystemForm = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();

    const [data, setData] = useState<ComputerizedSystem | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    const handleUpdated = useCallback((updated: ComputerizedSystem) => {
        setData(prev =>
            prev
                ? produce(prev, draft => {
                    Object.assign(draft as any, updated);
                })
                : updated
        );
    }, []);
    
    const handleReload = useCallback(() => {
        const sysId = Number(id);
        if (Number.isNaN(sysId)) return;
        
        dispatch(loadComputerizedSystemById(sysId))
            .then(unwrapResult)
            .then((dto) => {
                setData(dto);
            })
            .catch((e) => {
                console.error("Failed to reload system:", e);
            });
    }, [id, dispatch]);

    useEffect(() => {
        let mounted = true;
        const sysId = Number(id);
        if (Number.isNaN(sysId)) {
            setError(new Error("Invalid id"));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        dispatch(loadComputerizedSystemById(sysId))
            .then(unwrapResult)
            .then((dto) => {
                if (!mounted) return;
                setData(dto);
            })
            .catch((e) => {
                if (!mounted) return;
                setError(e);
            })
            .finally(() => mounted && setIsLoading(false));

        return () => {
            mounted = false;
        };
    }, [id, dispatch]);

    if (isLoading) return <LoadingContainer />;
    if (error) return <ErrorContainer error={error} />;
    if (!data) return null;

    return <ComputerizedSystemFormInternal system={data} onUpdated={handleUpdated} onReload={handleReload}
    />;
};

const fmtDate = (d?: string | Date | null) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d) : d;
    return isNaN(dt.getTime()) ? "—" : format(dt, "dd-MM-yyyy");
};

export const ComputerizedSystemFormInternal = ({ system, onUpdated, onReload }: { 
    system: ComputerizedSystem; 
    onUpdated: (updated: ComputerizedSystem) => void;
    onReload: () => void;
}) => {
    const [tab, setTab] = useState(0);
    const { locale } = useAppSelector(selectSettings);

    return (
        <Paper elevation={0} sx={{ p: 0 }}>
            <ComputerizedSystemHead system={system} />

            {/* Zakładki */}
            <Box >
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                >
                    <Tab
                        label={
                            <Localized id="cs-tab-info">
                                <span>_Informacje o systemie</span>
                            </Localized>
                        }
                    />
                    <Tab
                        label={
                            <Localized id="cs-tab-components">
                                <span>_Składniki systemu</span>
                            </Localized>
                        }
                    />
                    <Tab
                        label={
                            <Localized id="cs-tab-validation">
                                <span>_Procesy walidacyjne</span>
                            </Localized>
                        }
                    />
                </Tabs>
            </Box>
            <Divider />

            <Box role="tabpanel" hidden={tab !== 0} sx={{ p: 1 }}>
                {tab === 0 &&
                    <ComputerizedSystemOverviewPanel
                        system={system!}
                        onUpdate={async (_patch) => {/* opcjonalnie zapis partial */ }}
                        onUpdated={onUpdated}
                    />
                }
            </Box>
            <Box role="tabpanel" hidden={tab !== 1} sx={{ p: 1 }}>
                {tab === 1 && <SystemComponentsPanel system={system} onReload={onReload} />}
            </Box>
            <Box role="tabpanel" hidden={tab !== 2} sx={{ p: 1 }}>
                {tab === 2 && <ValidationProcessesPanel systemId={system.id} />}
            </Box>
        </Paper>
    );
};

export const ComputerizedSystemHead = (props: { system: ComputerizedSystem }) => {
    const { system } = props

    const { locale } = useAppSelector(selectSettings);

    return <Box
        sx={{
            px: 0.5,
            py: 1.5,
            bgcolor: "grey.100",
        }}
    >
        <Box
            sx={{
                px: 1,
                py: 1,
                bgcolor: "grey.100",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <Chip
                    size="small"
                    icon={<DnsIcon fontSize="small" />}
                    label={
                        <Localized id="cs-computerized-system">
                            <span>_System skomputeryzowany</span>
                        </Localized>
                    }
                    variant="outlined"

                />
            </Stack>

            <Box
                sx={(t) => ({
                    bgcolor: "white",
                    px: 1,
                    py: 1,
                    borderRadius: 1,
                    border: `1px solid ${t.palette.divider}`,
                })}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        fontSize: 20,
                    }}
                >
                    {system.name}
                </Typography>
            </Box>
        </Box>

        <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 2 }}
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ ml: 1.5 }}
        >
            {/* Struktura */}
            <Stack direction="row" spacing={1} alignItems="center">
                <AccountTreeIcon fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                    <Localized id="cs-structure">_Struktura</Localized>:
                </Typography>
                <Typography variant="body2">
                    {system.structureId ? <StructureName structureId={system.structureId} /> : "—"}
                </Typography>
            </Stack>

            {/* Data utworzenia */}
            <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthIcon fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                    <Localized id="registration-date">Data utworzenia</Localized>:
                </Typography>
                <Typography variant="body2">
                    {fmtDate((system as any).createDate ?? (system as any).createdAtUtc)}
                </Typography>
            </Stack>

            {/* Status walidacji */}
            <Stack direction="row" spacing={1} alignItems="center">
                <FactCheckIcon fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                    <Localized id="cs-validation-status">Status walidacji</Localized>:
                </Typography>
                <ObjectValidationStatusChip
                    status={(system as any).validationStatus as ObjectValidationStatus}
                    lang={/* "pl" | "en" */ (locale === "pl" ? "pl" : "en")}
                />
            </Stack>

            {/* GAMP 5 */}
            <Stack direction="row" spacing={1} alignItems="center">
                <CategoryIcon fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                    GAMP 5:
                </Typography>
                <GampCategoryChip
                    category={(system as any).gampCategory as GampCategory}
                    lang={/* "pl" | "en" */ (locale === "pl" ? "pl" : "en")}
                    withTooltip
                />
            </Stack>
        </Stack>
    </Box>
}
